import { FilterOptions, PagingInfo, ErrorDetail } from './LockDesigner';

/**
 * Admin Tool - search result
 */
export class LockDesignerAdminAsset {
    /**
     * Product / asset identifier
     */
    id?: string;

    /**
     * Display name
     */
    displayName?: string | null;

    /**
     * Model number
     */
    modelNumber?: string | null;

    /**
     * Asset type (see enum below)
     */
    assetType?: LockDesignerAssetType;

    /**
     * Scene7 Id, which will be either for the interior or exterior view
     */
    scene7Id?: string | null;

    scene7IdInside?: string | null;

    scene7IdOutside?: string | null;

    exterior?: boolean;
    /**
     * Thumbnail URL to display in the Vue component
     */
    imageUrl?: string | null;

    /**
     * ImageUrlInside URL to display in the Vue component
     */
    imageUrlInside?: string | null;

    /**
     * ImageUrlOutside URL to display in the Vue component
     */
    imageUrlOutside?: string | null;

    /**
     * Image geometry
     */
    geometry?: LockDesignerGeometry

    /**
     * X position
     */
    x?: number;

    /**
     * Y position
     */
    y?: number;

    /**
     * Width
     */
    width?: number;

    /**
     * Alt X position
     */
    altX?: number;

    /**
     * Alt Y position
     */
    altY?: number;

    /**
     * Alt Width
     */
    altWidth?: number;

    /**
     * Date/Time the asset was last modified. If never modified, this will be null
     */
    adminToolLastModified?: string | null;
}

/**
 * Admin Tool - search request
 */
export class LockDesignerAdminSearchRequest {
    constructor(assetType: string, searchTerm: string, excludeModified: boolean, filterOptions: { pageNumber: number; resultsPerPage: number }) {
        this.assetType = assetType;
        this.searchTerm = searchTerm;
        this.excludeModified = excludeModified;
        this.filterOptions = new FilterOptions(filterOptions.pageNumber, filterOptions.resultsPerPage);
    }

    /**
     * Should assets returned be for inside or outside of the door
     */
    inside: boolean;

    /**
     * (Optional) Search term
     */
    searchTerm: string | null;

    /**
     * Asset type to return results for (see enum below)
     */
    assetType: string;

    /**
     * Flag indicating if only assets that have never been updated in the Admin Tool should be returned
     */
    excludeModified: boolean;

    /**
     * Pagination
     */
    filterOptions: FilterOptions | null;
}

/**
 * Admin Tool - search response
 */
export class LockDesignerAdminSearchResponse {
    /**
     * Pagination information
     */
    paging: PagingInfo;

    /**
     * Collection of search results
     */
    results: LockDesignerAdminAsset[];
}

/**
 * Admin Tool - update request
 */
export class LockDesignerAdminUpdateRequest {
    /**
     * (Optional) Updates selected latch
     */
    latch: LockDesignerAdminAsset | null;

    /**
     * (Optional) Updates selected knob, lever or handleset
     */
    handle: LockDesignerAdminAsset | null;

    /**
     * (Optional) Updates selected rose or plate
     */
    escutcheon: LockDesignerAdminAsset | null;

    /**
     * (Optional) Updates selected deadbolt
     */
    privacyBolt: LockDesignerAdminAsset | null;

    /**
     * (Optional) Updates selected backplate
     */
    backplate: LockDesignerAdminAsset | null;
}

/**
 * Admin Tool - update response
 */
export class LockDesignerAdminUpdateResponse {
    /**
     * Results for handle update. Will be null if request.handle was null.
     */
    handleResults: ErrorDetail;

    /**
     * Results for escutcheon update. Will be null if request.escutcheon was null.
     */
    escutcheonResults: ErrorDetail;

    /**
     * Results for deadbolt update. Will be null if request.deadbolt was null.
     */
    deadboltResults: ErrorDetail;

    /**
     * Results for turn knob update. Will be null if request.turnKnob was null.
     */
    turnKnobResults: ErrorDetail;

    /**
     * Results for backplate update. Will be null if request.backplate was null.
     */
    backplateResults: ErrorDetail;

    /**
     * Handleset results
     */
    handlesetResults: ErrorDetail;

    /**
     * Latch results
     */
    latchResults: ErrorDetail;
}

/**
 * Enum for possible asset types
 */
export enum LockDesignerAssetType {
    Door,
    Latch,

    // Handle is parent
    Handle,
    Knob,
    Lever,
    Handleset,

    // Escutcheon is parent
    Escutcheon,
    StandardRose,
    SmallBoreRose,
    NineInchRose,
    LargeRose,
    FiveEighthRose,
    LargeEscutcheonSmallBoreRose,

    Deadbolt,

    // BoltTurnPiece is the parent
    BoltTurnPiece,
    TurnKnob,
    TurnPieceSet,

    Backplate
}

/**
 * Image geometry
 */
export enum LockDesignerGeometry {
    // Uses X, Y, and Width for both sides.
    Standard,

    // Uses X, Y, and Width for the outside and Alt X, Alt Y, Alt Width for the inside.
    SideVariation,

    // Uses Width for both sides, X and Y for tubular double-bore, and Alt X, Alt Y for mortise, ignoring Alt Width.
    MortisePosition
}
